import DataTable from 'datatables.net-bs5';
import 'datatables.net-bs5/css/dataTables.bootstrap5.css';
import frFR from 'datatables.net-plugins/i18n/fr-FR.mjs';
import deDE from 'datatables.net-plugins/i18n/de-DE.mjs';
import enGB from 'datatables.net-plugins/i18n/en-GB.mjs';
import './datatable.css'

// DataTables bundled translations keyed by the 2-letter language code.
// No CDN fetch: avoids the blocking i18n warning when offline.
const DT_LANGUAGES = {fr: frFR, de: deDE, en: enGB};

// Resolve the DataTables language pack from the app's current locale
// (getCurrentLocale is a global defined in the legacy functions.js, e.g. "de",
// "fr", "de_CH"). Falls back to French if unknown.
function resolveDtLanguage() {
    let code = 'fr';
    try {
        const loc = (typeof window.getCurrentLocale === 'function' ? window.getCurrentLocale() : '') || '';
        code = loc.toLowerCase().split(/[_-]/)[0] || 'fr';
    } catch (e) {
        // getCurrentLocale unavailable: keep the French default.
    }
    return DT_LANGUAGES[code] || frFR;
}

/**
 * Alpine.js DataTable component
 *
 * Column behaviour is controlled via CSS classes on <th> elements:
 *   no-sort    — disables sorting on that column
 *   no-search  — disables the filter input/select on that column
 *   hidden-col — hides the column entirely
 *   dt-select  — renders a select dropdown instead of a text input
 */
export default (customOptions = {}) => ({
    dataTable: null,
    init() {
        const table = this.$el.matches('table')
            ? this.$el
            : this.$el.querySelector('table');
        if (!table) {
            console.warn('No table found in datatable component');
            return;
        }

        const thead = table.querySelector('thead');

        // Clone the first header row to use as the filter row.
        // Cloning preserves CSS classes (no-search, dt-select, etc.)
        // so initComplete can read them from the original header cells.
        if (thead && !thead.querySelector('tr.dt-filters')) {
            const filterRow = thead.querySelector('tr').cloneNode(true);
            filterRow.classList.add('dt-filters');
            filterRow.querySelectorAll('th').forEach(th => {
                th.textContent = '';
            });
            thead.appendChild(filterRow);
        }
        const that = this;

        // --- Generic state persistence (sort, page length, page, filters) ---
        // Storage key priority: data-state-key attribute > table id > page path.
        // Give the table an id (or data-state-key) when several tables can share
        // the same URL, otherwise their saved states would collide.
        const stateKey = 'kibe-dt/' + (table.dataset.stateKey || table.id || window.location.pathname);
        const stateMaxAgeMs = 2 * 60 * 60 * 1000;

        const defaultOptions = {
            // Locale-aware bundled translations, matched to the current app language.
            language: {
                ...resolveDtLanguage(),
                emptyTable: '<i class="fas fa-info-circle"></i>' + getMessage('allgemein.dataTable.list.zeroRecords')
            },
            pageLength: 10,
            lengthMenu: [5, 10, 25, 50, 100],
            order: [[0, 'asc']],
            stateSave: true,
            stateSaveCallback: function (settings, data) {
                try {
                    localStorage.setItem(stateKey, JSON.stringify(data));
                } catch (e) {
                    // Storage unavailable or full: state is simply not persisted.
                }
            },
            stateLoadCallback: function () {
                try {
                    const data = JSON.parse(localStorage.getItem(stateKey));
                    if (data && data.time && new Date().getTime() - data.time > stateMaxAgeMs) {
                        return null;
                    }
                    return data;
                } catch (e) {
                    return null;
                }
            },
            orderCellsTop: true,
            responsive: true,
            columnDefs: [
                {orderable: false, targets: 'no-sort'},
                {searchable: false, targets: 'no-search'},
                {visible: false, targets: 'hidden-col'}
            ],
            initComplete: function () {
                const api = this.api();
                that.dtReady = true;
                api.columns().every(function () {
                    const column = this;
                    const colIdx = column.index();
                    const headerTh = column.header(); // <th> from first (title) row
                    const filterRow = thead.querySelector('tr.dt-filters');
                    const cell = filterRow
                        ? filterRow.querySelectorAll('th')[colIdx]
                        : null;

                    if (!cell) return;

                    if (headerTh.classList.contains('no-search')) {
                        return;
                    }

                    const columnTitle = headerTh.textContent.trim();

                    if (headerTh.classList.contains('dt-select')) {
                        const select = document.createElement('select');
                        select.className = 'form-select form-select-sm';
                        select.style.cssText = 'color: #212529; background-color: #fff; border: 1px solid #ced4da;';
                        select.add(new Option('', ''));
                        cell.replaceChildren(select);

                        // Read visible text from actual TD nodes instead of raw data(),
                        // so HTML content (badges, icons) is stripped correctly.
                        const uniqueValues = new Set();
                        column.nodes().each(function (td) {
                            const text = td.textContent.trim();
                            if (text) uniqueValues.add(text);
                        });

                        Array.from(uniqueValues).sort().forEach(val => {
                            select.add(new Option(val, val));
                        });

                        // Restore the saved column filter: the state itself was
                        // already applied before initComplete, sync the UI with it.
                        const savedSelectSearch = column.search();
                        if (savedSelectSearch) {
                            const savedVal = savedSelectSearch.replace(/^\^/, '').replace(/\$$/, '');
                            if (!Array.from(select.options).some(o => o.value === savedVal)) {
                                select.add(new Option(savedVal, savedVal));
                            }
                            select.value = savedVal;
                        }

                        select.addEventListener('change', function () {
                            column
                                .search(select.value ? `^${select.value}$` : '', true, false)
                                .draw();
                        });

                    } else {
                        const input = document.createElement('input');
                        input.type = 'text';
                        input.className = 'form-control form-control-sm';
                        input.placeholder = columnTitle;
                        input.style.cssText = 'color: #212529; background-color: #fff; border: 1px solid #ced4da;';
                        cell.replaceChildren(input);

                        // Restore the saved column filter: the state itself was
                        // already applied before initComplete, sync the UI with it.
                        const savedInputSearch = column.search();
                        if (savedInputSearch) {
                            const m = savedInputSearch.match(/^\(\(\((.*)\)\)\)$/);
                            input.value = m ? m[1] : savedInputSearch;
                            input.title = input.value;
                        }

                        input.addEventListener('keyup', function (e) {
                            e.stopPropagation();
                            input.title = input.value;
                            const cursorPos = input.selectionStart;
                            const val = input.value;
                            column
                                .search(
                                    val !== '' ? `(((${val})))` : '',
                                    val !== '',
                                    val === ''
                                )
                                .draw();
                            input.focus();
                            input.setSelectionRange(cursorPos, cursorPos);
                        });

                        input.addEventListener('change', function (e) {
                            e.stopPropagation();
                        });
                    }
                });

                // Workaround issue "Cannot read properties of null (reading 'includes')" in bootstrap5:
                // Set autocomplet=off to avoid null exception
                that.$el.querySelectorAll('input.form-control').forEach(input => {input.autocomplete = 'off';});

                // Apply default column search declared via data-default-search-column / data-default-search-value.
                // Also syncs the filter select so the UI reflects the active filter.
                // Skipped when a saved state exists, so it never overrides user filters.
                const defaultCol = parseInt(table.dataset.defaultSearchColumn);
                const defaultVal = table.dataset.defaultSearchValue;
                if (!isNaN(defaultCol) && defaultVal && !api.state.loaded()) {
                    api.column(defaultCol).search('^' + defaultVal + '$', true, false).draw();
                    const filterRow = thead.querySelector('tr.dt-filters');
                    if (filterRow) {
                        const filterTh = filterRow.querySelectorAll('th')[defaultCol];
                        const sel = filterTh && filterTh.querySelector('select');
                        if (sel) sel.value = defaultVal;
                    }
                }
            }
        };

        const mergedColumnDefs = [
            ...defaultOptions.columnDefs,
            ...(customOptions.columnDefs || [])
        ];

        const mergedInitComplete = customOptions.initComplete
            ? function () {
                defaultOptions.initComplete.call(this);
                customOptions.initComplete.call(this);
            }
            : defaultOptions.initComplete;

        const merged = Object.assign({}, defaultOptions, customOptions, {
            columnDefs: mergedColumnDefs,
            initComplete: mergedInitComplete
        });

        this.dataTable = new DataTable(table, merged);

        // HTMX only wires up rows present in the DOM when the page first loads
        // (page 1). Client-side pagination/sorting/filtering rebuilds the <tr>
        // nodes for other pages, so their hx-get/hx-post buttons keep the
        // attributes but lose the listeners and become dead. Re-process the
        // table on every draw so freshly rendered rows get bound. htmx.process
        // is idempotent, so already-bound rows are left untouched.
        if (window.htmx) {
            this.dataTable.on('draw.dt', () => window.htmx.process(table));
        }
    },
    reinit() {
        if (!this.dtReady) return;
        this.destroy();
        this.$nextTick(() => this.init());
    },
    destroy() {
        if (this.dataTable && this.dtReady) {
            try {
                this.dataTable.destroy(false);
            } catch(e) {}
            this.dataTable = null;
            this.dtReady = false;
        }
    }
});